// System-owned composition asset registry, expressed as a CSS custom-property block embedded
// in the composition HTML: `<style data-codeo-assets>:root{ --asset-<id>: url("data:…") }</style>`.
//
// Why in the HTML (not a side table): the composition the agent edits IS what the preview
// renders and what gets saved to GCS for the final render, so a block living in currentHtml
// rides along to both with no separate injection points. The agent never authors or sees the
// block — get_video elides it (see elideAssetDefs) — it just references assets by
// `var(--asset-<id>)`, so the heavy data URIs never enter the agent's token budget, and a
// reused asset never repeats its URI.

const ASSET_STYLE_RE = /<style data-codeo-assets>[\s\S]*?<\/style>/i;

export function stripAssetDefs(html: string): string {
    return html.replace(ASSET_STYLE_RE, '');
}

export function buildAssetDefsBlock(assets: Record<string, string>): string {
    const vars = Object.entries(assets)
        .map(([id, uri]) => `  --asset-${id}: url("${uri}");`)
        .join('\n');
    return `<style data-codeo-assets>\n:root {\n${vars}\n}\n</style>`;
}

// Ensure the HTML carries an up-to-date asset defs block for `assets` (or none when empty).
// Idempotent: strips any existing block first, so repeated writes don't accumulate.
export function withAssetDefs(html: string, assets: Record<string, string>): string {
    const stripped = stripAssetDefs(html);
    if (Object.keys(assets).length === 0) return stripped;
    const block = buildAssetDefsBlock(assets);
    if (/<head[^>]*>/i.test(stripped)) {
        return stripped.replace(/<head([^>]*)>/i, `<head$1>\n${block}`);
    }
    if (/<body[^>]*>/i.test(stripped)) {
        return stripped.replace(/<body([^>]*)>/i, `${block}\n<body$1>`);
    }
    return `${block}${stripped}`;
}

// Re-derive the registry from a composition's defs block (used on load, since only currentHtml
// is persisted — the assets map is reconstructed from it).
export function parseAssetDefs(html: string): Record<string, string> {
    const block = html.match(ASSET_STYLE_RE)?.[0];
    if (!block) return {};
    const assets: Record<string, string> = {};
    for (const m of block.matchAll(/--asset-([\w-]+)\s*:\s*url\(["']?(.*?)["']?\)\s*;/g)) {
        assets[m[1]] = m[2];
    }
    return assets;
}

// For get_video composition reads: replace the defs block's body with a short summary of available handles,
// so the agent sees what it can reference (var(--asset-<id>)) without the data URIs.
export function elideAssetDefs(html: string): string {
    return html.replace(ASSET_STYLE_RE, (block) => {
        const ids = [...block.matchAll(/--asset-([\w-]+)\s*:/g)].map((m) => m[1]);
        const summary = ids.length
            ? `asset vars (reference via var(--asset-<id>)): ${ids.join(', ')}`
            : 'no assets';
        return `<style data-codeo-assets>/* ${summary} */</style>`;
    });
}
